import {
  keepPreviousData,
  queryOptions,
  useMutation,
} from "@tanstack/react-query";
import { type AxiosResponse } from "axios";

import type {
  CreateTravelerBodyInputDto,
  FindUserTravelersOutputDto,
  SingleTravelerOutputDto,
  UpdateTravelerBodyInputDto,
} from "@/app/lib/types/codegen";

import api from "../api/api";
import { queryClient } from "../query";
import {
  type TravellerRemovedEventData,
  type TravellerUpdatedEventData,
} from "../types/types";

export const travellersQueryOptions = () =>
  queryOptions({
    queryKey: ["travelers"],
    queryFn: ({ signal }) =>
      api.get<FindUserTravelersOutputDto>("/travelers", {
        signal,
        params: {
          page: 1,
          perPage: 20,
        },
      }),
    placeholderData: keepPreviousData,
    select: (response) => response.data.data.travelers,
  });

export const useDeleteTravellerMutation = () =>
  useMutation({
    mutationFn: (travellerId: string) =>
      api.delete(`/travelers/${travellerId}`),
    onSuccess: (_, travellerId) => {
      queryClient.invalidateQueries({
        queryKey: travellersQueryOptions().queryKey,
      });

      const event = new CustomEvent<TravellerRemovedEventData>(
        "travellerremoved",
        {
          detail: { travellerId },
        },
      );

      window.dispatchEvent(event);
    },
  });

export const useUpdateTravellerMutation = () =>
  useMutation({
    mutationFn: ({
      travellerId,
      data,
    }: {
      travellerId: string;
      data: UpdateTravelerBodyInputDto;
    }) => api.put<SingleTravelerOutputDto>(`/travelers/${travellerId}`, data),
    onMutate: ({ travellerId }) => {
      const cache = queryClient
        .getQueryCache()
        .find<AxiosResponse<FindUserTravelersOutputDto>>({
          queryKey: travellersQueryOptions().queryKey,
        });

      const travellerOldData = cache?.state.data?.data.data.travelers.find(
        (traveller) => traveller.id === travellerId,
      );

      return travellerOldData;
    },
    onSuccess: (response, variables, travellerOldData) => {
      const event = new CustomEvent<TravellerUpdatedEventData>(
        "travellerupdated",
        {
          detail: {
            travellerOldData,
            travellerNewData: response.data.data,
          },
        },
      );

      window.dispatchEvent(event);

      queryClient.invalidateQueries({
        queryKey: travellersQueryOptions().queryKey,
      });
    },
  });

export const useNewTravellerMutation = () =>
  useMutation({
    mutationFn: (body: CreateTravelerBodyInputDto) =>
      api.post<SingleTravelerOutputDto>("/travelers", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: travellersQueryOptions().queryKey,
      });
    },
  });
